import React, { useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import './manualOnboarding.css';
import CustomButton from '../../../../../components/MasterComponent/Button/CustomButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useFetchData } from '../../../../../hooks/useFetchData';
import { ManualArnProps } from '../../../../../schemas/account.schema';
import { config, extractAccountIdFromArn, triggerAwsJob } from '../config';
import Loading from '../../../../../components/Loading/Loading';
import { useHistory } from 'react-router-dom';
import { RoutesFun } from '../../../../../schemas';
import { AccountForm } from '../../../../../components/AccountOnboarding/AccountForm';

interface CompleteAccountSetupProps {
  onBack: () => void;
}

const CompleteAccountSetup: React.FC<CompleteAccountSetupProps> = ({
  onBack,
}) => {
  const [awsArn, setAwsArn] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const { fetch, data, loader: isSubmitting, error } = useFetchData({
    fetchData: (formData: ManualArnProps) =>
      config.addManualArnHandler(formData),
    fetchOnMount: false,
  });

  const errorMessage =
  error?.error?.message ||
  'There was an issue connecting your AWS account to CloudForestX.';

  const history = useHistory();

  const handleSubmit = async () => {
    try {
      if (isFormValid) {
        const payload: ManualArnProps = {
          AccountArn: awsArn,
          Name: companyName,
        };

        await fetch(payload);

        const accountId = extractAccountIdFromArn(awsArn);

        if (accountId) {
          triggerAwsJob(accountId);
        }
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <Box className="completeSetupContainer">
      <Paper elevation={0} className="instructionsPaper">
        <p className="instructions">
          Complete your AWS account onboarding by providing your AWS Role ARN
          and your company name. CloudForestX will use this information to
          connect to your AWS account and analyze your cost data.
        </p>
      </Paper>

      {isSubmitting ? (
        <Paper elevation={1} sx={{ height: '28rem' }} className="formPaper">
          <Loading />
        </Paper>
      ) : data || error ? (
        <Box
          className={`accountCompletionContainer ${
            error ? 'error' : 'success'
          }`}
        >
          {error ? (
            <CancelOutlinedIcon className="statusIcon" />
          ) : (
            <CheckCircleOutlineIcon className="statusIcon" />
          )}
          <Typography variant="h4" className="statusTitle">
            {error ? 'Account Setup Failed!' : 'Account Setup Complete!'}
          </Typography>
          <Typography variant="body2" className="statusMessage">
            {error
            ? errorMessage
            : 'Your CloudForestX onboarding has been successfully completed! You will be able to view our resources and services within 24 hours, and detailed AWS cost breakdowns will be available within 24 hours.'}
          </Typography>

          {!error && (
            <CustomButton
              buttonType="contained"
              onClickHandler={() => history.push(RoutesFun().Dashboard)}
              style={{ marginTop: '1rem' }}
            >
              Go To Dashboard
            </CustomButton>
          )}
        </Box>
      ) : (
        <AccountForm
          onAwsArnChange={setAwsArn}
          onCompanyNameChange={setCompanyName}
          onFormValidityChange={setIsFormValid}
        />
      )}

      <Box className="navigationButtons">
        <CustomButton buttonType="outlined" onClickHandler={onBack}>
          Back
        </CustomButton>

        {(data || !error) && (!data || error) && (
          <CustomButton
            buttonType="contained"
            onClickHandler={handleSubmit}
            disabled={!isFormValid}
          >
            Complete Onboarding
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};

export default CompleteAccountSetup;
